import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  StatisticsAlbumInfo,
  StatisticsDataItem,
  StatisticsTrackInfo,
  useMusicApi
} from "../../api/useMusicApi";
import { useLoading } from "../../hooks/use-loading";
import { toast } from "react-toastify";
import { Card } from "../../components/Card/Card";
import { CardBody } from "../../components/Card/CardBody";
import { CardHeader } from "../../components/Card/CardHeader";
import { Link } from "../../components/Link";
import cn from "classnames";
import {
  CartesianGrid,
  ResponsiveContainer,
  Line,
  LineChart,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";
import { InputSelect } from "../../components/InputSelect";
import { StatisticPeriod, StatisticPlatform } from "./StatisticsPage";
import { StringParam, useQueryParams, withDefault } from "use-query-params";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons/faSpinner";
import { usePageHelper } from "../../hooks/use-page-helper";

export const AlbumStatisticPage: React.FC = () => {
  const { albumId, trackId } = useParams();
  const navigate = useNavigate();
  const { loadings, startLoading, stopLoading } = useLoading({
    album: true,
    track: true,
    statistics: true
  });

  const [query, setQuery] = useQueryParams({
    platform: withDefault(StringParam, StatisticPlatform.VK_BOOM),
    period: withDefault(StringParam, StatisticPeriod.LAST_WEEK),
    release: withDefault(StringParam, null)
  });

  const {
    getStatisticsForAlbum,
    getStatisticsForTrack,
    getStatisticsDataItems
  } = useMusicApi();
  const [album, setAlbum] = useState<StatisticsAlbumInfo | null>(null);
  const [trackInfo, setTrack] = useState<StatisticsTrackInfo | null>(null);
  const [statisticsData, setStatisticsData] = useState<StatisticsDataItem[]>(
    []
  );

  const { t } = usePageHelper(trackInfo?.track_title ?? "", true);

  useEffect(() => {
    if (albumId) {
      fetchAlbumInfo(albumId).then();
    }
  }, [albumId]);

  useEffect(() => {
    if (
      trackId == null &&
      album?.streams != null &&
      album?.streams?.length > 0
    ) {
      navigate(`/statistics/${albumId}/${album.streams[0].track_id}`);
    }
  }, [album]);

  useEffect(() => {
    if (trackId) {
      fetchTrackInfo(trackId).then();
    }
  }, [trackId]);

  useEffect(() => {
    if (trackId) {
      fetchStatisticsData(
        trackId,
        query.release,
        query.platform as any,
        query.period as any
      ).then();
    }
  }, [trackId, query.release, query.platform, query.period]);

  const fetchAlbumInfo = useCallback(async (albumId: number | string) => {
    try {
      startLoading("album");
      const res = await getStatisticsForAlbum(albumId);
      setAlbum(res);
    } catch (e) {
      toast.error(t("error"));
    } finally {
      stopLoading("album");
    }
  }, []);

  const fetchTrackInfo = useCallback(async (trackId: number | string) => {
    try {
      startLoading("track");
      const res = await getStatisticsForTrack(trackId);
      setTrack(res);
    } catch (e) {
      toast.error(t("error"));
    } finally {
      stopLoading("track");
    }
  }, []);

  const fetchStatisticsData = useCallback(
    async (
      trackId: number | string,
      releaseId: number | string | null,
      platform?: StatisticPlatform,
      period?: StatisticPeriod
    ) => {
      try {
        startLoading("statistics");
        const res = await getStatisticsDataItems(
          trackId,
          releaseId,
          platform,
          period
        );
        setStatisticsData(res);
      } catch (e) {
        toast.error(t("error"));
      } finally {
        stopLoading("statistics");
      }
    },
    []
  );

  return (
    <>
      <Card>
        <CardHeader>
          <div>
            <div className={"text-2xl text-muted"}>
              <span className={"font-bold"}>{album?.album_title}</span>
            </div>
            <div className={"text-muted"}>
              <span className={""}>{album?.artist_name}</span>
            </div>
          </div>
        </CardHeader>
        <CardBody>
          <div className={"flex gap-3"}>
            <div className={"flex flex-col"} style={{ width: 350 }}>
              <div className={"font-bold px-2 text-muted"}>{t("tracks")}:</div>
              {album?.streams.map((t, index) => {
                return (
                  <Link
                    to={`/statistics/${albumId}/${t.track_id}?platfrom=${query.platform}&period=${query.period}`}
                    key={t.track_id}
                    className={cn("cursor-pointer px-3 text-sm", {
                      "bg-gray-100": index % 2 == 0 && trackId != t.track_id,
                      "bg-blue-500 text-white": trackId == t.track_id
                    })}
                    style={{ paddingTop: 10, paddingBottom: 10 }}
                  >
                    {index + 1}. {t.track_title}
                  </Link>
                );
              })}
            </div>
            <div>
              <div className={cn("flex gap-2 my-4")} style={{ marginLeft: 65 }}>
                <InputSelect
                  isClearable
                  style={{ width: "250px" }}
                  isLoading={loadings.track}
                  disabled={loadings.track || loadings.statistics}
                  placeholder={t("choose_release").toString()}
                  value={query.release}
                  options={
                    trackInfo?.releases?.map((r) => ({
                      label: r.track_title,
                      value: r.release_id
                    })) ?? []
                  }
                  onChange={(v) =>
                    setQuery({ release: v?.value as string | null })
                  }
                />
                <InputSelect
                  disabled={loadings.track || loadings.statistics}
                  value={query.platform}
                  options={[
                    { label: "VK + BOOM", value: StatisticPlatform.VK_BOOM },
                    { label: "OK + BOOM", value: StatisticPlatform.OK_BOOM }
                  ]}
                  onChange={(v) =>
                    setQuery({ platform: v.value as StatisticPlatform })
                  }
                />
                <InputSelect
                  disabled={loadings.track || loadings.statistics}
                  value={query.period}
                  options={[
                    { label: t("last_week"), value: StatisticPeriod.LAST_WEEK },
                    {
                      label: t("last_month"),
                      value: StatisticPeriod.LAST_MONTH
                    }
                  ]}
                  onChange={(v) =>
                    setQuery({ period: v.value as StatisticPeriod })
                  }
                />
                {loadings.statistics && (
                  <div className={"flex align-items-center"}>
                    <FontAwesomeIcon
                      spin
                      icon={faSpinner}
                      size={"xl"}
                      className={"text-muted"}
                    />
                  </div>
                )}
              </div>
              <ResponsiveContainer width={"100%"} height={500}>
                <LineChart
                  data={statisticsData.map((d) => ({
                    name: new Intl.DateTimeFormat("ru-RU", {
                      day: "2-digit",
                      month: "2-digit",
                      year: undefined
                    }).format(new Date(d.date)),
                    streams: d.amount
                  }))}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis dataKey={"streams"} />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="streams"
                    label={false}
                    stroke="rgb(59, 130, 246)"
                    strokeWidth={6}
                    activeDot={{ r: 8 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </CardBody>
      </Card>
    </>
  );
};
